/**
 * PendingStatusStrip — 38px status strip rendered flush below
 * ResultsTopNav on pending / failed result surfaces.
 *
 * Phase 1a.5-vi expansion: now supports three states:
 *   - "analysing"      : spinner + "Analysing your outfit"
 *   - "failed_analysis": alert + "Couldn't analyse" + Try again / Email
 *   - "failed_load"    : cloud-off + "Couldn't load this outfit" + Email
 *
 * Retry is omitted from failed_load because regenerateOutfit cannot fix
 * a hydration / RLS failure on an existing row.
 */
import { useEffect, useState, type CSSProperties } from "react";

interface PendingStatusStripProps {
  status: "analysing" | "failed_analysis" | "failed_load";
  outfitId?: string | null;
  notifyEnabled?: boolean;
  onNotifyTap?: () => void;
  onRetry?: () => void;
  canRetry?: boolean;
}

function useNarrowViewport(breakpoint = 360): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => setNarrow(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);
  return narrow;
}

export default function PendingStatusStrip({
  status,
  outfitId = null,
  notifyEnabled = false,
  onNotifyTap,
  onRetry,
  canRetry = false,
}: PendingStatusStripProps) {
  const narrow = useNarrowViewport();
  const isAnalysing = status === "analysing";
  const isFailedLoad = status === "failed_load";
  const isFailedAnalysis = status === "failed_analysis";

  // WAVE-1 — MM:SS countdown timer for the analysing branch.
  // Starts at 2:00. When it reaches 0:10 with the strip still mounted
  // (i.e. still analysing), swaps the label to a sticky "Hang tight,
  // almost there" and adds 60s to the remaining time. Repeats indefinitely
  // — timer never hits 0. Self-contained; no meta plumbing required
  // because mount time ≈ analysis-start time on all five render paths.
  const [mountedAt] = useState(() => Date.now());
  const [remaining, setRemaining] = useState(180);
  const [escalations, setEscalations] = useState(0);
  void mountedAt; // retained for future analytics; suppresses unused warn
  useEffect(() => {
    if (!isAnalysing) return;
    const id = window.setInterval(() => {
      setRemaining((r) => {
        if (r <= 10) {
          setEscalations((n) => n + 1);
          return r + 60;
        }
        return r - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [isAnalysing]);
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, "0");
  const timerText = `${mm}:${ss}`;
  const analysingLabel =
    escalations > 0 ? "Hang tight, almost there" : "Analysing your outfit";

  const label = isAnalysing
    ? analysingLabel
    : isFailedLoad
      ? "Couldn't load this outfit"
      : "Couldn't analyse";

  const iconClass = isAnalysing
    ? "ti ti-loader-2"
    : isFailedLoad
      ? "ti ti-cloud-off"
      : "ti ti-alert-triangle";

  const showNotify = isAnalysing && notifyEnabled;
  const showRetry = isFailedAnalysis && canRetry && typeof onRetry === "function";
  const showEmail = isFailedLoad || (isFailedAnalysis && !canRetry);
  // failed_analysis with retry shows BOTH retry + email per spec
  const showEmailNextToRetry = isFailedAnalysis && canRetry;

  const subject = encodeURIComponent(
    isFailedLoad
      ? `Support: Couldn't load outfit [${outfitId ?? "unknown"}]`
      : `Support: Outfit Failed [${outfitId ?? "unknown"}]`,
  );
  const mailto = `mailto:support@like2fit.com?subject=${subject}`;

  return (
    <>
      <style>{`
        @keyframes l2f-status-strip-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div
        role={isAnalysing ? "status" : "alert"}
        aria-live={isAnalysing ? "polite" : "assertive"}
        style={{
          height: 38,
          width: "100%",
          padding: "0 16px",
          background: "#F0E8DC",
          borderBottom: "0.5px solid #E8E2D9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <i
            className={iconClass}
            style={{
              fontSize: 18,
              color: "#C84B2F",
              lineHeight: 1,
              animation: isAnalysing
                ? "l2f-status-strip-spin 1s linear infinite"
                : undefined,
            }}
          />
          <span
            className="l2f-type-caption"
            style={{
              color: "#4A4540",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          {isAnalysing && (
            <span
              aria-label={`Estimated ${mm} minutes ${ss} seconds remaining`}
              style={{
                color: "#C84B2F",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              {timerText}
            </span>
          )}
          {showNotify && (
            <button
              type="button"
              onClick={onNotifyTap}
              style={ctaStyle}
            >
              Notify when ready ▸
            </button>
          )}
          {showRetry && (
            <button
              type="button"
              onClick={onRetry}
              style={ctaStyle}
            >
              Try again ▸
            </button>
          )}
          {(showEmail || showEmailNextToRetry) && (
            narrow ? (
              <a
                href={mailto}
                aria-label="Email support"
                style={{ ...ctaStyle, display: "inline-flex", alignItems: "center" }}
              >
                <i className="ti ti-mail" style={{ fontSize: 18 }} />
              </a>
            ) : (
              <a href={mailto} style={ctaStyle}>
                {showEmailNextToRetry ? "Email ▸" : "Email support ▸"}
              </a>
            )
          )}
        </div>
      </div>
    </>
  );
}

const ctaStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "#C84B2F",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1,
  textDecoration: "none",
};
